import { useCallback, useEffect } from 'react';

import { useToast } from 'components/hooks/useToast';

function flattenFilters(filterModel) {
  const flattened = {};

  Object.entries(filterModel).forEach(([key, value]) => {
    if (value.filterType === 'multi' && Array.isArray(value.filterModels)) {
      // Filter out null values
      const validFilters = value.filterModels.filter(Boolean);

      // If there are valid filters, replace "multi" with them
      if (validFilters.length > 0) {
        flattened[key] = validFilters;
      }
    } else {
      // Keep other filters unchanged
      flattened[key] = value;
    }
  });

  return flattened;
}

export const useServerSideDatasource = ({ gridRef, fetchRows, successFallback, failCallback = null }) => {
  const toast = useToast();
  const getServerSideDatasource = useCallback(
    () => ({
      getRows: async (params) => {
        try {
          const {
            request: { filterModel },
          } = params;

          const flattenedFilterModel = flattenFilters(filterModel);
          const filterObject = flattenedFilterModel?.['ag-Grid-AutoColumn']?.[0];
          const { filter, type, filterType } = filterObject || {};

          const res = await fetchRows({
            filterModel: filter
              ? {
                  QUESTION_DESCRIPTION: {
                    filterType,
                    type: type === 'contains' ? 'equals' : type,
                    filter,
                    values: [],
                    filterTo: [],
                  },
                }
              : {},
          });
          if (!res) {
            params.fail();
            toast({
              title: 'An unknown error has occurred, please try again',
              status: 'error',
            });
            return;
          }

          if (typeof successFallback === 'function') {
            successFallback({ rows: res, params });
          } else {
            params.success({
              rowData: res?.entity?.rows ?? [],
              rowCount: res?.entity?.rows?.length ?? 0,
              pivotResultFields: res?.entity?.pivotFields,
            });
          }
        } catch (error) {
          console.error('Error fetching rows:', error);
          if (typeof failCallback === 'function') {
            failCallback();
          } else {
            params.fail();
          }
        }
      },
    }),

    // don't add toast as dependency, it will cause multiple api call
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [failCallback, fetchRows, successFallback]
  );

  useEffect(() => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    const datasource = getServerSideDatasource();

    // register the datasource with the grid
    gridApi.setGridOption('serverSideDatasource', datasource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getServerSideDatasource, gridRef.current?.api]);
};
